/**
 * 化身（Impersonation）相关 React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, API_ENDPOINTS } from '@/lib/api-client'

export interface ImpersonationHistoryItem {
  id: string
  admin?: {
    id: string
    name: string
    phone: string
  }
  impersonatedUser: {
    id: string
    name: string
    phone: string
    roles: string[]
  }
  reason: string | null
  startedAt: string
  endedAt: string | null
  actionsCount: number
}

export interface ImpersonationHistoryResponse {
  data: ImpersonationHistoryItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface ImpersonationActionItem {
  id: string
  action: string
  endpoint: string
  method: string
  metadata: unknown
  performedAt: string
}

export interface ImpersonationActionsResponse {
  data: ImpersonationActionItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface StartImpersonationPayload {
  targetUserId: string
  /** 化身原因，后端 ImpersonateRequest 可选（仅记审计 metadata） */
  reason?: string
}

export interface StartImpersonationResponse {
  sessionId: string
  accessToken: string
  refreshToken: string
  expiresIn: number
  impersonatedUser: {
    id: string
    name: string
    phone: string
    roles: string[]
  }
}

export interface ExitImpersonationResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

/**
 * 化身历史列表
 */
export function useImpersonationHistory(filters?: {
  adminId?: string
  page?: number
  limit?: number
  enabled?: boolean
}) {
  const { enabled = true } = filters ?? {}
  return useQuery<ImpersonationHistoryResponse>({
    queryKey: [
      'impersonation-history',
      filters?.adminId,
      filters?.page,
      filters?.limit,
    ],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.adminId) params.append('adminId', filters.adminId)
      if (filters?.page) params.append('page', String(filters.page))
      if (filters?.limit) params.append('limit', String(filters.limit))
      const qs = params.toString()
      return apiClient.get<ImpersonationHistoryResponse>(
        `${API_ENDPOINTS.impersonation.history}${qs ? `?${qs}` : ''}`
      )
    },
    enabled,
  })
}

/**
 * 某次化身期间的操作列表
 */
export function useImpersonationActions(
  sessionId: string | null | undefined,
  params?: { page?: number; limit?: number }
) {
  return useQuery<ImpersonationActionsResponse>({
    queryKey: ['impersonation-actions', sessionId, params?.page, params?.limit],
    queryFn: async () => {
      const qs = new URLSearchParams()
      if (params?.page) qs.append('page', String(params.page))
      if (params?.limit) qs.append('limit', String(params.limit))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return apiClient.get<ImpersonationActionsResponse>(
        `${API_ENDPOINTS.impersonation.actions(sessionId!)}${suffix}`
      )
    },
    enabled: Boolean(sessionId),
  })
}

/**
 * 开始化身（仅 ADMIN）
 * 成功后会切换 localStorage 中的 token，由调用方决定刷新时机
 */
export function useStartImpersonation() {
  return useMutation<StartImpersonationResponse, Error, StartImpersonationPayload>({
    mutationFn: (payload) =>
      apiClient.post<StartImpersonationResponse>(
        API_ENDPOINTS.impersonation.start,
        // 后端 ImpersonateRequest 字段名为 userId；此前前端误传 targetUserId 导致 400「Field required」
        { userId: payload.targetUserId, reason: payload.reason }
      ),
  })
}

/**
 * 退出化身
 */
export function useExitImpersonation() {
  const qc = useQueryClient()
  return useMutation<ExitImpersonationResponse, Error, { impersonationId: string }>({
    mutationFn: (payload) =>
      apiClient.post<ExitImpersonationResponse>(
        API_ENDPOINTS.impersonation.exit,
        payload
      ),
    onSuccess: () => {
      qc.clear()
    },
  })
}
